// @ts-check

import { registerDefaults } from "../composables/defaults";
import { registerAppComponents } from "./app";
import { registerComponents } from "./component";
import { loadConfig } from "./config";

/**
 * @typedef {import("vue").ComponentObjectPropsOptions} ComponentPropsOptions
 * @typedef {Record<string, string[]>} ComponentAliases
 * @typedef {{ global?: ComponentPropsOptions} & Record<string, ComponentPropsOptions>} SparkleElementsDefaults
 * @typedef {{ aliases?: ComponentAliases, defaults?: SparkleElementsDefaults}} SparkleElementsOptions
 */

/**
 * Initializes the custom elements.
 *
 * @returns {Promise<void>}
 */
export async function initialize() {
  const config = await loadConfig();

  return await createSparkleElements(config);
}

/**
 * Registers all custom elements.
 *
 * @param {SparkleElementsOptions} options - Default values for the component options.
 */
export async function createSparkleElements(options) {
  const { defaults = {}, aliases = {} } = options ?? {};

  // Register the defaults for the custom element and its child components.
  // This is done before registering the components to ensure that the defaults are applied to the components.
  registerDefaults(defaults);

  // Register all components in the components and apps directories.
  // This is done in parallel to speed up the registration process.
  await Promise.all([registerComponents(defaults, aliases), registerAppComponents(defaults)]);

  markAsInitialized();
}

/**
 * Marks the custom elements as initialized.
 *
 * Enables the custom elements to be visible and allows the slot content to be displayed.
 * It also indicates to the wider app that the custom elements have been loaded.
 *
 * @see app/assets/stylesheets/custom_elements/setup.scss.erb
 * @see app/frontend/src/custom_elements/framework#registerCustomElement
 */
function markAsInitialized() {
  // Set the visibility of the custom elements to visible.
  // This is required to prevent a flickering effect when the custom elements are loaded.
  document.documentElement.style.setProperty("--sp-ce-visibility", "visible");
  document.documentElement.style.setProperty("--sp-ce-slot-display", "unset");

  // Indicates to the wider app that the custom elements have been loaded
  Object.assign(window, { spElementsInitialized: true });
}
